import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { Intervento } from 'api'
import { ChevronDownIcon, FilterIcon, Grid, List, XIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { z } from 'zod'

import {
  formatDateForAPI,
  formatDateRangeForBreadcrumb,
  groupInterventionsByDate,
} from '@/lib/date-utils'
import { dayjs } from '@/lib/dayjs'
import { useDexieQuery } from '@/hooks/use-dexie-query'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { DateRangePicker } from '@/components/date-range-picker'
import { PageTitle } from '@/components/layout/page-title'
import { StatoFilterSelect } from '@/features/interventi/components/stato-filter-select'
import { applyInterventoFilters } from '@/features/interventi/utils/apply-intervento-filters'
import {
  legacyStatoFilter,
  parseStatoFilter,
} from '@/features/interventi/utils/stato'
import { CalendarView } from '@/routes/_authenticated/report/-components/calendar-view'
import { ListView } from '@/routes/_authenticated/report/-components/list-view'

export const Route = createFileRoute('/_authenticated/report/')({
  component: ReportsPage,
  ssr: false,
  validateSearch: z
    .object({
      view: z.enum(['details', 'list']).optional(),
      dateFrom: z.coerce.string().optional(),
      dateTo: z.coerce.string().optional(),
      // A value of the Stato filter (statoFilters), as on the interventi list
      stato: z.coerce.string().optional(),
      // Old links (bookmarks): read as the stato they meant, then replaced
      esito: z.coerce.string().optional(),
    })
    .optional(),
})

/**
 * Hook to get all unique intervention dates using efficient cursor-based aggregation
 * Uses eachKey() to iterate through indexed date values without loading full records
 * Optionally filters by date range using efficient indexed queries
 */
function useInterventiDatesList(dateRange?: DateRange): string[] | undefined {
  const dateRangeKey = useMemo(
    () =>
      dateRange
        ? `${dateRange.from?.toISOString()}-${dateRange.to?.toISOString()}`
        : 'all',
    [dateRange]
  )

  return useDexieQuery(
    async (db) => {
      const dateSet = new Set<string>()

      // Use eachKey() to efficiently iterate through indexed date keys
      // This is the most efficient way to aggregate unique dates
      let query = db.interventi.orderBy('data').reverse()

      // Use efficient indexed range filtering when date range is provided
      if (dateRange?.from && dateRange?.to) {
        const startOfRange = dayjs(dateRange.from).startOf('day').toDate()
        const endOfRange = dayjs(dateRange.to).endOf('day').toDate()

        // Use indexed .where().between() instead of .filter() for better performance
        query = db.interventi
          .where('data')
          .between(startOfRange, endOfRange, true, true)
          .reverse()
      }

      await query.eachKey((dateKey) => {
        if (dateKey instanceof Date) {
          const dateStr = formatDateForAPI(dateKey)
          dateSet.add(dateStr)
        }
      })

      // Return sorted array of unique dates (already in reverse order from query)
      return Array.from(dateSet)
    },
    [dateRangeKey]
  )
}

/**
 * Hook to fetch interventions for a specific date range using indexed queries
 * Returns empty array if no date range is provided
 */
function useInterventiByDateRange(
  dateRange: DateRange | undefined,
  stato: string | undefined
): Intervento[] | undefined {
  const dateRangeKey = useMemo(
    () =>
      dateRange
        ? `${dateRange.from?.toISOString()}-${dateRange.to?.toISOString()}`
        : null,
    [dateRange]
  )

  return useDexieQuery(
    (db) => {
      if (!dateRange?.from || !dateRange?.to) return []

      const startOfRange = dayjs(dateRange.from).startOf('day').toDate()
      const endOfRange = dayjs(dateRange.to).endOf('day').toDate()

      // The range on its index, then the stato as the interventi list filters
      // it (and without the rows deleted on this device). Chained, not awaited
      // in an async function: the live query follows the queries in then()
      return applyInterventoFilters(
        db,
        db.interventi.where('data').between(startOfRange, endOfRange, true, true),
        { stato }
      ).then((query) => query.toArray())
    },
    [dateRangeKey, stato]
  )
}

/**
 * ReportsPage component displays interventions in calendar and list views
 * Now supports date range selection instead of single date
 */
function ReportsPage() {
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const isMobile = useIsMobile()
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  const { view = 'details', dateFrom, dateTo, esito } = searchParams ?? {}
  // A stato the filter doesn't know (a hand-edited link) is no filter: here,
  // and in the downloads, which the export API would refuse with it
  const requestedStato =
    searchParams?.stato ?? legacyStatoFilter(undefined, esito)
  const stato = parseStatoFilter(requestedStato) ? requestedStato : undefined

  // An old link's ?esito= becomes the ?stato= it meant, in place: left in the
  // address it would come back whenever the stato is cleared
  useEffect(() => {
    if (esito === undefined) return
    navigate({
      search: (s) => ({
        ...s,
        stato: s?.stato ?? legacyStatoFilter(undefined, s?.esito),
        esito: undefined,
      }),
      replace: true,
    })
  }, [esito, navigate])

  // Derive date range from URL parameters, default to today
  const selectedDateRange = useMemo((): DateRange | undefined => {
    if (dateFrom && dateTo) {
      return {
        from: dayjs(dateFrom).toDate(),
        to: dayjs(dateTo).toDate(),
      }
    }
    if (dateFrom) {
      const fromDate = dayjs(dateFrom).toDate()
      return {
        from: fromDate,
        to: fromDate, // Single day range
      }
    }
    // Default to today if no date is specified
    const today = new Date()
    return {
      from: today,
      to: today,
    }
  }, [dateFrom, dateTo])

  // Optimized queries: Use date range for filtering
  const allInterventiDates = useInterventiDatesList() // All dates for DateRangePicker highlighting
  const selectedRangeInterventions = useInterventiByDateRange(
    selectedDateRange,
    stato
  )

  const handleTabChange = useCallback(
    (newView: string) => {
      navigate({
        search: (s) => ({
          ...s,
          view: newView as 'details' | 'list',
        }),
      })
    },
    [navigate]
  )

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      navigate({
        search: (s) => ({
          ...s,
          dateFrom: range?.from ? formatDateForAPI(range.from) : undefined,
          dateTo: range?.to ? formatDateForAPI(range.to) : undefined,
        }),
      })
    },
    [navigate]
  )

  const handleClearFilters = useCallback(() => {
    navigate({
      search: (s) => ({
        ...s,
        stato: undefined,
        esito: undefined,
        dateFrom: undefined,
        dateTo: undefined,
      }),
    })
  }, [navigate])

  // Create grouped data for both views: the range's interventi, of the stato
  // filtered (in the query, as the list and the exports read it)
  const filteredInterventions = useMemo(
    () => selectedRangeInterventions || [],
    [selectedRangeInterventions]
  )

  const groupedData = useMemo((): Record<string, Intervento[]> => {
    return groupInterventionsByDate(filteredInterventions)
  }, [filteredInterventions])
  const filteredInterventiDates = useMemo(
    () => Object.keys(groupedData),
    [groupedData]
  )

  const filtersActive = Boolean(stato || dateFrom || dateTo)

  // Memoize breadcrumbs with selected date range - always include Report as prefix
  const breadcrumbItems = useMemo(() => {
    const items = [{ name: 'Report', to: '/report' as const }]

    // Always add selected date range as breadcrumb since we default to today
    if (selectedDateRange?.from) {
      const breadcrumbName = formatDateRangeForBreadcrumb(selectedDateRange)
      items.push({
        name: breadcrumbName,
        to: '/report' as const,
      })
    }

    return items
  }, [selectedDateRange])

  return (
    <div className="flex h-full flex-col">
      <Breadcrumbs items={breadcrumbItems} />

      <Tabs
        value={view}
        onValueChange={handleTabChange}
        className="flex h-full w-full grow flex-col"
      >
        <div className="mt-4 mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex w-full items-center justify-between gap-3 lg:w-auto">
            <PageTitle className="my-0 shrink-0">Report</PageTitle>

            {isMobile && (
              <TabsList className="grid w-auto shrink-0 grid-cols-2">
                <TabsTrigger value="details">
                  <Grid className="h-4 w-4" />
                  <span className="ml-2 hidden md:inline">Dettagli</span>
                </TabsTrigger>
                <TabsTrigger value="list">
                  <List className="h-4 w-4" />
                  <span className="ml-2 hidden md:inline">Elenco</span>
                </TabsTrigger>
              </TabsList>
            )}
          </div>

          <Collapsible
            open={isMobile ? filtersExpanded || filtersActive : true}
            onOpenChange={isMobile ? setFiltersExpanded : undefined}
            className="w-full lg:w-auto"
          >
            <div className="flex w-full flex-col gap-3 lg:items-end">
              {isMobile && (
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <FilterIcon className="size-4" />
                      <span>Filtri</span>
                      {filtersActive && (
                        <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                          {[stato, dateFrom, dateTo].filter(Boolean).length}
                        </span>
                      )}
                    </div>
                    <ChevronDownIcon
                      className={`size-4 transition-transform ${filtersExpanded || filtersActive ? 'rotate-180' : ''}`}
                    />
                  </Button>
                </CollapsibleTrigger>
              )}

              <CollapsibleContent
                className={isMobile ? 'mt-2 w-full' : 'block'}
              >
                <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center lg:justify-end">
                  <StatoFilterSelect
                    className="w-full sm:w-[220px]"
                    value={stato}
                    onChange={(value) =>
                      navigate({
                        search: (s) => ({
                          ...s,
                          stato: value,
                          esito: undefined,
                        }),
                      })
                    }
                  />

                  <DateRangePicker
                    className="w-full sm:w-auto"
                    date={selectedDateRange}
                    onChange={handleDateRangeChange}
                    placeholder="Seleziona periodo"
                    disableFutureDates={true}
                    interventiDates={allInterventiDates || []}
                    iconOnlyOnMobile={false}
                    maxRangeDays={90}
                  />

                  {filtersActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      className="w-full sm:w-auto sm:whitespace-nowrap"
                    >
                      <XIcon className="size-4" />
                      <span className="ml-1">Cancella filtri</span>
                    </Button>
                  )}

                  {!isMobile && (
                    <TabsList className="grid w-auto shrink-0 grid-cols-2">
                      <TabsTrigger value="details">
                        <Grid className="h-4 w-4" />
                        <span className="ml-2">Dettagli</span>
                      </TabsTrigger>
                      <TabsTrigger value="list">
                        <List className="h-4 w-4" />
                        <span className="ml-2">Elenco</span>
                      </TabsTrigger>
                    </TabsList>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        <TabsContent value="details" className="mt-0 md:overflow-hidden">
          <CalendarView
            groupedInterventi={groupedData}
            selectedDateRange={selectedDateRange}
            stato={stato}
          />
        </TabsContent>

        <TabsContent value="list" className="mt-0 overflow-hidden">
          <ListView
            groupedInterventi={groupedData}
            interventiDates={filteredInterventiDates}
            selectedDateRange={selectedDateRange}
            stato={stato}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
